import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import api from '../api/client';
import fetchTaskListPage from '../api/fetchTaskListPage';
import MarkdownText from './MarkdownText';
import CollaborativeTaskSearchModal from './CollaborativeTaskSearchModal';
import { taskNavRef } from '../utils/taskNav';

const PAGE_SIZE = 200;
const DEFAULT_FILTERS = {
  search: '',
  sourceNumber: '',
  tagsAll: [],
  tagsAny: [],
  tagsNot: [],
  sortField: 'site',
  sortOrder: 'asc',
};

/**
 * Правая панель: список всех задач с поиском и фильтром по тегу.
 * Используется только преподавателем.
 */
const CollaborativeAllTasksPanel = forwardRef(function CollaborativeAllTasksPanel({
  sessionId,
  addedTaskIds,
  onAdded,
  refreshNonce,
}, ref) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextPath, setNextPath] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [autoLoadNonce, setAutoLoadNonce] = useState(0);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [jumpNumber, setJumpNumber] = useState('');
  const [jumpTargetId, setJumpTargetId] = useState(null);

  // теги для UI фильтров теперь выбираются через TagTokenInput в модалке

  useEffect(() => {
    if (refreshNonce == null) return;
    if (!hasSubmitted) return;
    startSearch(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  useEffect(() => {
    if (!hasSubmitted) return;
    const el = document.getElementById('collab-all-tasks-load-more-sentinel');
    if (!el || !hasMore || loadingMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries && entries[0];
        if (first?.isIntersecting) setAutoLoadNonce((n) => n + 1);
      },
      { root: null, rootMargin: '200px', threshold: 0.01 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasSubmitted, hasMore, loadingMore, tasks.length]);

  useEffect(() => {
    if (!hasSubmitted) return;
    if (!autoLoadNonce) return;
    loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoadNonce]);

  function resetFilters() {
    setFilters(DEFAULT_FILTERS);
    setHasSubmitted(false);
    setError('');
    setTasks([]);
    setHasMore(false);
    setNextPath(null);
    setTotalCount(null);
    setAutoLoadNonce(0);
    setJumpNumber('');
    setJumpTargetId(null);
  }

  function hasAnyFilter(nextFilters) {
    const s = (nextFilters?.search || '').trim();
    const sourceNumber = (nextFilters?.sourceNumber || '').trim();
    const tagsAll = Array.isArray(nextFilters?.tagsAll) ? nextFilters.tagsAll : [];
    const tagsAny = Array.isArray(nextFilters?.tagsAny) ? nextFilters.tagsAny : [];
    const tagsNot = Array.isArray(nextFilters?.tagsNot) ? nextFilters.tagsNot : [];
    return Boolean(s) || Boolean(sourceNumber) || tagsAll.length > 0 || tagsAny.length > 0 || tagsNot.length > 0;
  }

  async function startSearch(nextFilters = filters) {
    setError('');
    setTasks([]);
    setHasMore(false);
    setNextPath(null);
    setTotalCount(null);
    setAutoLoadNonce(0);

    if (!hasAnyFilter(nextFilters)) {
      setError('Введите условия и нажмите «Применить».');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('include_statement', '1');
      const search = (nextFilters?.search || '').trim();
      if (search) params.set('search', search);
      const sourceNumber = (nextFilters?.sourceNumber || '').trim();
      if (sourceNumber) params.set('source_number', sourceNumber);
      for (const t of nextFilters?.tagsAll || []) {
        if (t?.slug) params.append('tag', t.slug);
      }
      for (const t of nextFilters?.tagsAny || []) {
        if (t?.slug) params.append('tag_any', t.slug);
      }
      for (const t of nextFilters?.tagsNot || []) {
        if (t?.slug) params.append('tag_not', t.slug);
      }
      params.set('catalog_sort', nextFilters?.sortField === 'source' ? 'source' : 'site');
      params.set('catalog_order', nextFilters?.sortOrder === 'desc' ? 'desc' : 'asc');
      params.set('ege_on_map', '1');
      const qs = params.toString();
      const catalogUrl = qs ? `/tasks/?${qs}` : '/tasks/';
      const { results, nextPath: next, count } = await fetchTaskListPage(catalogUrl, { pageSize: PAGE_SIZE });
      setTasks(results);
      setNextPath(next);
      setHasMore(Boolean(next));
      setTotalCount(typeof count === 'number' ? count : null);
      if (sourceNumber) {
        jumpToNumber(sourceNumber, results);
      }
    } catch {
      setError('Не удалось загрузить задачи.');
      setTasks([]);
      setHasMore(false);
      setNextPath(null);
      setTotalCount(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!hasSubmitted || loadingMore || loading || !hasMore || !nextPath) return;
    setLoadingMore(true);
    setError('');
    try {
      const { results, nextPath: next, count } = await fetchTaskListPage(nextPath, { pageSize: PAGE_SIZE });
      let merged = [];
      setTasks((prev) => {
        merged = [...prev, ...(results || [])];
        return merged;
      });
      setNextPath(next);
      setHasMore(Boolean(next));
      if (typeof count === 'number') setTotalCount(count);
      if (jumpNumber.trim() && !findTaskByNumber(jumpNumber, tasks)) {
        jumpToNumber(jumpNumber, merged);
      }
    } catch {
      setError('Не удалось загрузить следующую страницу.');
    } finally {
      setLoadingMore(false);
    }
  }

  async function addToSession(taskId) {
    if (!sessionId || taskId == null) return;
    setError('');
    try {
      await api.post(`/collaborative/sessions/${sessionId}/tasks/`, {
        task_id: taskId,
      });
      if (typeof onAdded === 'function') onAdded();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' && detail.trim() ? detail : 'Не удалось добавить задачу в сессию.');
    }
  }

  function openEditor(taskIdOrSlug) {
    // На странице редактирования уже используется slug; здесь считаем, что он есть
    const slug = typeof taskIdOrSlug === 'string' ? taskIdOrSlug : null;
    if (!slug) return;
    const url = `/teacher/tasks?task=${encodeURIComponent(slug)}`;
    window.open(url, '_blank', 'noopener');
  }

  const addedSet = useMemo(() => new Set(addedTaskIds || []), [addedTaskIds]);

  useImperativeHandle(ref, () => ({
    expandAllStatements() {
      setExpandedIds(new Set((tasks || []).map((t) => t.id)));
    },
    collapseAllStatements() {
      setExpandedIds(new Set());
    },
    openSearchModal() {
      setSearchModalOpen(true);
    },
  }), [tasks]);

  function toggleExpanded(taskId) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  function formatTaskNumber(task) {
    const n = task?.source_number;
    if (n != null && String(n).trim() !== '') return String(n);
    if (task?.slug) return String(task.slug);
    if (task?.id != null) return `#${task.id}`;
    return '—';
  }

  function taskNumberKeys(task) {
    const keys = [];
    const source = task?.source_number != null ? String(task.source_number).trim() : '';
    if (source) keys.push(source);
    if (task?.slug) keys.push(String(task.slug));
    if (task?.id != null) {
      keys.push(String(task.id));
      keys.push(`#${task.id}`);
    }
    return keys;
  }

  function findTaskByNumber(raw, list = tasks) {
    const q = String(raw || '').trim();
    if (!q) return null;
    const exact = list.find((task) => taskNumberKeys(task).some((key) => key.toLowerCase() === q.toLowerCase()));
    if (exact) return exact;
    return list.find((task) =>
      taskNumberKeys(task).some((key) => key.toLowerCase().startsWith(q.toLowerCase()))
    ) || null;
  }

  function jumpToNumber(raw, list = tasks) {
    const match = findTaskByNumber(raw, list);
    setJumpTargetId(match?.id ?? null);
    if (!match) return;
    requestAnimationFrame(() => {
      document.getElementById(`collab-all-task-${match.id}`)?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    });
  }

  function applyNumberFilter(raw = jumpNumber) {
    const sourceNumber = String(raw || '').trim();
    const next = { ...filters, sourceNumber };
    setFilters(next);
    setJumpNumber(sourceNumber);
    if (!hasAnyFilter(next)) {
      setError('Введите номер задачи или откройте поиск и задайте условия.');
      return;
    }
    setHasSubmitted(true);
    startSearch(next);
  }

  function taskTitle(task) {
    const t = typeof task?.title === 'string' ? task.title.trim() : '';
    return t;
  }

  function statementRaw(task) {
    if (typeof task?.statement === 'string' && task.statement.trim()) return task.statement;
    if (typeof task?.statement_preview === 'string' && task.statement_preview.trim()) return task.statement_preview;
    return '';
  }

  return (
    <div className="collab-all-tasks-panel">
      <CollaborativeTaskSearchModal
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        initialFilters={filters}
        onReset={() => {
          resetFilters();
          setSearchModalOpen(false);
        }}
        onApply={(next) => {
          setFilters(next);
          setJumpNumber((next?.sourceNumber || '').trim());
          setHasSubmitted(true);
          setSearchModalOpen(false);
          startSearch(next);
        }}
      />

      <form
        className="collab-all-tasks-jump"
        onSubmit={(e) => {
          e.preventDefault();
          const match = findTaskByNumber(jumpNumber);
          if (match) {
            jumpToNumber(jumpNumber);
            return;
          }
          applyNumberFilter(jumpNumber);
        }}
      >
        <label htmlFor="collab-task-number-jump">Номер задачи</label>
        <input
          id="collab-task-number-jump"
          type="text"
          inputMode="numeric"
          className="form-input"
          placeholder="Например, 354"
          value={jumpNumber}
          onChange={(e) => {
            const value = e.target.value;
            setJumpNumber(value);
            jumpToNumber(value);
          }}
        />
        <button type="submit" className="btn btn-outline btn-xs">
          Перейти
        </button>
      </form>

      {error && <div className="alert alert-error">{error}</div>}
      {!hasSubmitted ? (
        <p className="text-secondary">
          Введите номер и нажмите «Перейти», либо откройте поиск и задайте теги.
        </p>
      ) : null}
      {loading && hasSubmitted && <p className="text-secondary">Загрузка задач...</p>}
      {!loading && hasSubmitted && tasks.length === 0 && !error && (
        <p className="text-secondary">Задачи не найдены.</p>
      )}

      <div className="collab-all-tasks-list">
        {tasks.map((task) => {
          const isAdded = addedSet.has(task.id);
          const isExpanded = expandedIds.has(task.id);
          const statement = statementRaw(task);
          const snippet = statement ? String(statement).slice(0, 200) : '';
          const canExpand = statement && String(statement).length > 200;
          const allTags = Array.isArray(task?.tags) ? task.tags : [];
          const visibleTags = allTags.slice(0, 4);
          const hiddenTagCount = Math.max(0, allTags.length - visibleTags.length);
          return (
            <div
              id={`collab-all-task-${task.id}`}
              key={task.id}
              className={`collab-all-task-card ${isAdded ? 'is-added' : ''} ${jumpTargetId === task.id ? 'is-jump-target' : ''}`}
            >
              <div className="collab-all-task-main">
                <div className="collab-all-task-head-row">
                  <div className="collab-all-task-head-left">
                    <span className="collab-all-task-number">{formatTaskNumber(task)}</span>
                    {visibleTags.length > 0 && (
                      <span className="collab-all-task-inline-tags" aria-label="Теги задачи">
                        {visibleTags.map((tag) => (
                          <span key={tag.id ?? tag.slug ?? tag.name} className="tag tag-compact" title={tag.name}>
                            {tag.name}
                          </span>
                        ))}
                        {hiddenTagCount > 0 && (
                          <span className="tag tag-compact tag-compact-more" title={`Ещё тегов: ${hiddenTagCount}`}>
                            +{hiddenTagCount}
                          </span>
                        )}
                      </span>
                    )}
                    {taskTitle(task) ? (
                      <span className="collab-all-task-name">{taskTitle(task)}</span>
                    ) : null}
                  </div>
                  <div className="collab-all-task-head-right">
                    <button
                      type="button"
                      className="btn btn-outline btn-xs btn-icon-only"
                      title={isAdded ? 'Добавить ещё в сессию' : 'Добавить в сессию'}
                      aria-label={isAdded ? 'Добавить ещё в сессию' : 'Добавить в сессию'}
                      onClick={() => {
                        addToSession(task.id);
                      }}
                    >
                      <i className="fa-solid fa-square-plus ui-icon" aria-hidden />
                    </button>
                    {task.id != null && (
                      <button
                        type="button"
                        className="btn btn-outline btn-xs btn-icon-only"
                        title="Редактировать задачу"
                        aria-label="Редактировать задачу"
                        onClick={() => openEditor(taskNavRef(task))}
                      >
                        <i className="fa-solid fa-pen ui-icon" aria-hidden />
                      </button>
                    )}
                  </div>
                </div>
                {statement ? (
                  <div className="collab-all-task-statement">
                    {!isExpanded ? (
                      <>
                        <div className="collab-all-task-statement-snippet">
                          {snippet}{canExpand ? '…' : ''}
                        </div>
                        {canExpand && (
                          <button
                            type="button"
                            className="btn btn-link btn-xs collab-all-task-statement-toggle"
                            onClick={() => toggleExpanded(task.id)}
                          >
                            Показать полностью
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <MarkdownText text={statement} className="collab-all-task-statement-md" />
                        <button
                          type="button"
                          className="btn btn-link btn-xs collab-all-task-statement-toggle"
                          onClick={() => toggleExpanded(task.id)}
                        >
                          Свернуть
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {hasSubmitted && tasks.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <div className="text-secondary" style={{ fontSize: '0.8rem', marginBottom: '0.35rem' }}>
            Показано: {tasks.length}
            {typeof totalCount === 'number' ? ` из ${totalCount}` : ''}
          </div>
          {hasMore ? (
            <>
              <div className="text-secondary" style={{ fontSize: '0.8rem', marginBottom: '0.35rem' }}>
                Есть ещё задачи — прокрутите вниз для подгрузки.
              </div>
              <button
                type="button"
                className="btn btn-outline btn-xs"
                disabled={loadingMore}
                onClick={loadMore}
              >
                {loadingMore ? 'Загрузка…' : 'Загрузить ещё'}
              </button>
              <div id="collab-all-tasks-load-more-sentinel" style={{ height: '1px' }} />
            </>
          ) : (
            <div className="text-secondary" style={{ fontSize: '0.8rem' }}>
              Это все задачи по текущему фильтру.
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default CollaborativeAllTasksPanel;

